/**
 * @fileoverview Indicadores do período: Entradas | Enviadas | Entregues |
 * Cliques | Conversões, cada um com a taxa que faz sentido para ele. Indicador
 * que depende de algo não ligado mostra "Não configurado" ou "Indisponível" no
 * lugar do número: zero ali seria um número inventado.
 */

import React from "react";
import { Tooltip } from "@material-ui/core";
import { HelpCircle } from "lucide-react";
import { i18n } from "../../../translate/i18n";
import { EMPTY, formatCurrency, formatNumber, formatPercent } from "./format";

const t = (key, options) => i18n.t(`flowResults.${key}`, options);

const Help = ({ classes, text }) => (
  <Tooltip title={text} arrow placement="top">
    <span className={classes.helpIcon} tabIndex={0} role="img" aria-label={text}>
      <HelpCircle size={13} />
    </span>
  </Tooltip>
);

const Card = ({ classes, name, value, sub, subFaint }) => (
  <div className={classes.metric}>
    <div className={classes.metricLabel}>
      {t(`metrics.${name}`)}
      <Help classes={classes} text={t(`metrics.help.${name}`)} />
    </div>
    <div className={classes.metricValue}>{value}</div>
    <div className={`${classes.metricSub} ${subFaint ? classes.metricSubFaint : ""}`}>{sub}</div>
  </div>
);

/** Indicador sem número: diz por quê e, quando dá para ligar, oferece o caminho. */
const UnavailableCard = ({ classes, name, state, hint, onConfigure }) => (
  <div className={classes.metric}>
    <div className={classes.metricLabel}>
      {t(`metrics.${name}`)}
      <Help classes={classes} text={t(`metrics.help.${name}`)} />
    </div>
    <div className={classes.metricUnavailable}>{t(`metrics.${state}`)}</div>
    <div className={classes.metricSub}>
      {hint}
      {state === "notConfigured" && onConfigure && (
        <button type="button" className={classes.textBtn} onClick={onConfigure}>
          {t("metrics.configure")}
        </button>
      )}
    </div>
  </div>
);

const rateText = (key, ratio) => (ratio === null || ratio === undefined ? EMPTY : t(`metrics.rate.${key}`, { value: formatPercent(ratio) }));

export const MetricCardsSkeleton = ({ classes }) => (
  <div className={classes.metrics} aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className={classes.metric}>
        <div className={classes.skeleton} style={{ width: 80, height: 12 }} />
        <div className={classes.skeleton} style={{ width: 96, height: 28, marginTop: 12 }} />
        <div className={classes.skeleton} style={{ width: 120, height: 12, marginTop: 10 }} />
      </div>
    ))}
  </div>
);

const MetricCards = ({ classes, data, onConfigure }) => {
  const { totals, rates, availability } = data;
  const configure = onConfigure;

  return (
    <>
      <div className={classes.metrics}>
        <Card classes={classes} name="entries" value={formatNumber(totals.entries)} sub={t("metrics.inPeriod")} subFaint />
        <Card
          classes={classes}
          name="sent"
          value={formatNumber(totals.sent)}
          sub={totals.sendFailed > 0 ? t("metrics.sendFailed", { count: totals.sendFailed }) : t("metrics.messages")}
          subFaint={!totals.sendFailed}
        />
        <Card classes={classes} name="delivered" value={formatNumber(totals.delivered)} sub={rateText("delivery", rates.delivery)} />

        {availability.clicks === "ok" ? (
          <Card classes={classes} name="clicks" value={formatNumber(totals.clicks)} sub={rateText("click", rates.click)} />
        ) : (
          <UnavailableCard
            classes={classes}
            name="clicks"
            state={availability.clicks === "unavailable" ? "unavailable" : "notConfigured"}
            hint={availability.clicks === "unavailable" ? t("metrics.clicksUnavailableHint") : t("metrics.clicksNotConfiguredHint")}
            onConfigure={configure}
          />
        )}

        {availability.conversions === "ok" ? (
          <Card
            classes={classes}
            name="conversions"
            value={formatNumber(totals.conversions)}
            sub={
              totals.conversions > 0
                ? `${rateText("conversion", rates.conversion)} · ${t("metrics.conversionValue", { value: formatCurrency(totals.conversionValue) })}`
                : rateText("conversion", rates.conversion)
            }
          />
        ) : (
          <UnavailableCard
            classes={classes}
            name="conversions"
            state="notConfigured"
            hint={t("metrics.conversionsNotConfiguredHint")}
            onConfigure={configure}
          />
        )}
      </div>

      <div className={classes.summaryLine}>
        <span className={classes.summaryItem}>
          <span className={classes.dotRunning} />
          {t("summary.running", { count: data.running })}
        </span>
        <span className={classes.summaryItem}>
          <span className={classes.dotOk} />
          {t("summary.completed", { count: totals.completed })}
        </span>
        <span className={classes.summaryItem}>
          <span className={classes.dotError} />
          {t("summary.failed", { count: totals.failed })}
        </span>
        <span className={classes.summaryItem}>
          <span className={classes.dotOff} />
          {t("summary.cancelled", { count: totals.cancelled })}
        </span>
        {totals.delivered > 0 && (
          <span className={classes.summaryItem}>{t("summary.read", { count: totals.read, value: formatPercent(rates.read) })}</span>
        )}
      </div>
    </>
  );
};

export default MetricCards;
